/**
 * Economía individual (V1.45) — resumen financiero por miembro de la unidad familiar.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, TrendingUp, TrendingDown, Scale, CalendarClock } from "lucide-react";
import api, { apiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useFamilyMembers } from "../components/MemberSelect";

const eur = (v) => (v ?? 0).toLocaleString("es-ES", { style: "currency", currency: "EUR" });
const firstOfMonth = () => new Date().toISOString().slice(0, 8) + "01";
const today = () => new Date().toISOString().slice(0, 10);

function Card({ label, value, icon: Icon, tone }) {
  return (
    <div className="border border-border rounded-lg p-4">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold flex items-center gap-1.5">
        {Icon && <Icon size={12} />} {label}
      </div>
      <div className={`text-xl font-heading font-extrabold tabular mt-1 ${tone || ""}`}>{value}</div>
    </div>
  );
}

export default function EconomiaMiembro() {
  const members = useFamilyMembers();
  const [memberId, setMemberId] = useState("");
  const [desde, setDesde] = useState(firstOfMonth());
  const [hasta, setHasta] = useState(today());
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!memberId && members.length > 0) setMemberId(members[0].id);
  }, [members]);

  useEffect(() => {
    if (!memberId) return;
    api.get("/members/economy", { params: { member_id: memberId, desde, hasta } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e)));
  }, [memberId, desde, hasta]);

  return (
    <div className="space-y-6" data-testid="economia-miembro-page">
      <div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tighter flex items-center gap-2"><Users size={26} /> Economía individual</h1>
        <p className="text-sm text-muted-foreground mt-1">Ingresos, gastos y fijos mensuales que pertenecen a cada miembro. Los movimientos "Común (familiar)" se consultan con su propia vista.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[220px_160px_160px] gap-2">
        <Select value={memberId || undefined} onValueChange={setMemberId}>
          <SelectTrigger data-testid="economy-member-select"><SelectValue placeholder="Elige miembro…" /></SelectTrigger>
          <SelectContent>
            {members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
            <SelectItem value="comun">Común (familiar)</SelectItem>
          </SelectContent>
        </Select>
        <Input type="date" data-testid="economy-desde" value={desde} onChange={(e) => setDesde(e.target.value)} />
        <Input type="date" data-testid="economy-hasta" value={hasta} onChange={(e) => setHasta(e.target.value)} />
      </div>

      {data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Card label="Ingresos (periodo)" value={eur(data.totales.ingresos)} icon={TrendingUp} tone="text-emerald-600" />
            <Card label="Gastos (periodo)" value={eur(data.totales.gastos)} icon={TrendingDown} tone="text-red-600" />
            <Card label="Balance" value={eur(data.totales.balance)} icon={Scale} tone={data.totales.balance >= 0 ? "text-emerald-600" : "text-red-600"} />
            <Card label="Fijos mensuales" value={eur(data.fijos_mensuales.total)} icon={CalendarClock} />
          </div>

          <div className="border border-border rounded-lg p-4" data-testid="economy-fijos">
            <div className="text-xs uppercase tracking-wider text-muted-foreground font-bold mb-2">Desglose de fijos mensuales de {data.member.name}</div>
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div>Compromisos <div className="font-semibold tabular">{eur(data.fijos_mensuales.compromisos)}</div><div className="text-[11px] text-muted-foreground">{data.contadores.compromisos} activos</div></div>
              <div>Obligaciones <div className="font-semibold tabular">{eur(data.fijos_mensuales.obligaciones)}</div><div className="text-[11px] text-muted-foreground">{data.contadores.obligaciones} activas</div></div>
              <div>Seguros <div className="font-semibold tabular">{eur(data.fijos_mensuales.seguros)}</div><div className="text-[11px] text-muted-foreground">{data.contadores.seguros} pólizas</div></div>
            </div>
          </div>

          <div className="border border-border rounded-lg" data-testid="economy-recientes">
            <div className="px-4 py-3 border-b border-border text-xs uppercase tracking-wider text-muted-foreground font-bold">Movimientos recientes del periodo</div>
            {data.recientes.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">Sin movimientos de {data.member.name} en el periodo seleccionado.</div>
            ) : (
              <div className="divide-y divide-border">
                {data.recientes.map((r, i) => (
                  <div key={i} className="flex items-center justify-between px-4 py-2 text-sm">
                    <div className="min-w-0">
                      <span className="font-medium">{r.concepto || "—"}</span>
                      <span className="text-[11px] text-muted-foreground ml-2">{r.fecha}{r.created_by ? ` · creado por ${r.created_by}` : ""}</span>
                    </div>
                    <span className={`font-semibold tabular shrink-0 ${r.importe >= 0 ? "text-emerald-600" : "text-red-600"}`}>{eur(r.importe)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
